/* eslint-disable max-len */
import {
  Button,
  Checkbox,
  Dropdown,
  Icons,
  Label,
  Spinner,
  Table,
} from "@myob/myob-widgets";
import React, { useMemo, useState } from "react";
import _ from "lodash";

import { ETimesheetStatusTypes } from "../../utils/enums/timesheetStatusTypes";
import { ITimesheet } from "./store/interfaces/ITimesheetModel";
import { allTimesheetsAreApproved, countSelectableLines } from "./utils";
import {
  calcNumSelected,
  calculateWidthPercentage,
  getTotalColNum,
  timesheetTableColumns,
} from "./styles/stylingUtils";
import { calculateTimesheetHrs, updateTimebillingPayload } from "../../utils/timesheetUtils";
import DeleteTimesheetModal from "./modals/DeleteTimesheetModal";
import EditTimesheetModal from "./modals/EditTimesheetModal";
import GeolocationModal from "./modals/GeolocationModal";
import PhotoVerificationModal from "./modals/PhotoVerificationModal";
import styles from "./styles/TimesheetWrapperV2.module.scss";


/**
 * Create the field content for the approval status column.
 * @param status Status of timesheet.
 * @param loading Loading Status of timesheet.
 */
// eslint-disable-next-line consistent-return
export const approvalStatusField = (status, loading) => {
  if (loading) {
    return (
      <div style={{ display: "inline-block" }}>
        <Spinner size="small" />
      </div>
    );
  }

  // eslint-disable-next-line default-case
  switch (status) {
    case "PENDING":
      return (
        <span>
          <Label color="orange">Pending</Label>
        </span>
      );
    case "APPROVED":
      return <span>Approved</span>;
    case "FAILED":
      return (
        <span className={styles.failed}>
          <span className={styles.errorIcon}>
            <Icons.Error />
          </span>
          {" "}
          <Label color="purple">Failed</Label>
        </span>
      );
  }
};

interface EvidenceCapturedProps {
  timesheet: ITimesheet,
  showGeolocation: () => void,
  showPhotoVerification: () => void,
  isDisabled: false,
}

export const EvidenceCaptured: React.FC<EvidenceCapturedProps> = ({
  showGeolocation, showPhotoVerification, timesheet, isDisabled,
}: EvidenceCapturedProps) => {
  if (timesheet.location.geolocationEnabled) {
    return (
      <Button
        type="link"
        onClick={showGeolocation}
      >
        View geolocation
      </Button>
    );
  }

  if (timesheet.location.photoCaptureEnabled) {
    return (
      <Button
        type="link"
        onClick={showPhotoVerification}
        disabled={isDisabled}
      >
        View photo verification
      </Button>
    );
  }

  return null;
};

interface TimesheetRowProps {
  d: ITimesheet,
  index: number,
  selectIndividualTimesheet: (x) => {},
  userId: string,
  editTimesheet: any,
  closeRow: Function,
  rosteredHours: boolean,
  photoCaptureDisabled: boolean,
}

/**
 * Normal table row in accordion.
 * @param d Data for row
 * @param index Index of row.
 * @param selectIndividualTimesheet Redux action to select an individual timesheet.
 * @param closeRow Callback to close expanded row after deleting the timesheet.
 */
export const TimesheetRow: React.FC<TimesheetRowProps> = ({
  d, index, selectIndividualTimesheet, userId, editTimesheet, closeRow, rosteredHours, photoCaptureDisabled,
}: TimesheetRowProps) => {
  // Columns depends on split and if the features is enabled for the location.
  const evidenceCaptured = d.location.geolocationEnabled || d.location.photoCaptureEnabled;
  const columns = timesheetTableColumns({ evidenceCaptured, rosteredHours });

  // Total width of the timesheet table row, inside accordion.
  const totalTimesheetHeaderWidths = useMemo(() => getTotalColNum(columns), []);

  // Array of width values for each column of the timesheet row, inside accordion.
  const totalTimesheetRowWidths = useMemo(() => columns.map((col) => calculateWidthPercentage(col.colNum, totalTimesheetHeaderWidths + 0.1)), []);

  const [showGeolocationModal, setShowGeoLocationModal] = useState(false);

  const [showPhotoModal, setShowPhotoModal] = useState(false);

  const dropdownItems = [
    <Dropdown.Item
      key="action1"
      label="Edit timesheet"
      value={{ intent: "editTimesheet", id: d.id }}
    />,
    <Dropdown.Item
      key="action2"
      label="Delete timesheet"
      value={{ intent: "deleteTimesheet", id: d.id }}
    />,
  ];

  // Modal toggle state
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const timezone = d.location.locationTimeZone;

  const toggleDeleteModal = () => {
    setShowDeleteModal(!showDeleteModal);
  };

  function onDropdownAction(event) {
    if (event.intent === "editTimesheet") {
      setShowEditModal(true);
    } else if (event.intent === "deleteTimesheet") {
      toggleDeleteModal();
    }
  }

  function cancelEditModal() {
    setShowEditModal(false);
  }

  function onEdit(event, timesheetObj) {
    // validation and transformation
    if (timesheetObj.allFieldsAreValid()) {
      const updatedTimesheet = timesheetObj.getTimesheet();
      const getTimesheetHrs = _.cloneDeep(updatedTimesheet);

      const {payload} = updatedTimesheet.activityId ?
        updateTimebillingPayload(getTimesheetHrs, timezone) :
        calculateTimesheetHrs(getTimesheetHrs, timezone);
      payload.timesheet.userId = userId;

      editTimesheet(userId, [payload]);
      setShowEditModal(false);
    }
  }

  return (
    <Table.Row key={d.id} isSelected={d.checked} rowData={{ id: d.id }}>
      <Table.RowItem width="auto" cellRole="checkbox" valign="middle">
        <Checkbox
          name={`${d.id}-select`}
          label={`Select row ${index}`}
          hideLabel
          onChange={() => selectIndividualTimesheet(d.id)}
          checked={d.checked}
          disabled={d.status === ETimesheetStatusTypes.APPROVED}
        />
      </Table.RowItem>
      {
        _.map(columns, (col, num) => (
          <Table.RowItem
            width={totalTimesheetRowWidths[num]}
            columnName={d[col.key]}
          >
            {col.format(d, () => setShowGeoLocationModal(true), () => setShowPhotoModal(true), photoCaptureDisabled)}
          </Table.RowItem>
        ))
      }
      <Table.RowItem width="auto" cellRole="actions" valign="middle">
        <Dropdown
          right
          items={dropdownItems}
          onSelect={(event) => onDropdownAction(event)}
          toggle={(
            <Dropdown.Toggle disabled={d.status === ETimesheetStatusTypes.APPROVED} type="clear" aria-label="more options" size="xs">
              <Icons.More size="16px" />
            </Dropdown.Toggle>
          )}
        />
      </Table.RowItem>
      { showGeolocationModal ? (
        <GeolocationModal
          onClose={() => setShowGeoLocationModal(false)}
          timesheet={d}
        />
      ) : null }
      { showPhotoModal ? (
        <PhotoVerificationModal
          onCancel={() => setShowPhotoModal(false)}
          timesheet={d}
        />
      ) : null }
      { showEditModal ? (
        <EditTimesheetModal
          timezone={timezone}
          onCancel={cancelEditModal}
          timesheet={d}
          onEdit={onEdit}
        />
      ) : null }
      <DeleteTimesheetModal
        onCancel={toggleDeleteModal}
        userId={userId}
        timesheetId={d.id}
        show={showDeleteModal}
        closeRow={closeRow}
      />
    </Table.Row>
  );
};

interface TimesheetHeaderProps {
  timesheets: ITimesheet[],
  id: number,
  selectTimesheetRow: (x, y) => {},
  rosteredHours: boolean,
}

/**
 * Header for inner table of accordion.
 * @param timesheet Timesheet data.
 * @param id Timesheet row id.
 * @param selectTimesheetRow Redux action to select an entire set of timesheets in inner table.
 */
export const TimesheetHeader: React.FC<TimesheetHeaderProps> = ({
  timesheets, id, selectTimesheetRow, rosteredHours,
}: TimesheetHeaderProps) => {
  const selectedCount = calcNumSelected(timesheets);
  const selectableCount = countSelectableLines(timesheets);
  const isChecked = timesheets.length && selectableCount !== 0 && selectedCount === selectableCount;

  const evidenceCaptured = (timesheets[0].location.geolocationEnabled || timesheets[0].location.photoCaptureEnabled);
  const columns = timesheetTableColumns({ evidenceCaptured, rosteredHours });


  // Total width of the timesheet table row, inside accordion.
  const totalTimesheetHeaderWidths = useMemo(() => getTotalColNum(columns), []);

  // Array of width values for each column of the timesheet row, inside accordion.
  const totalTimesheetRowWidths = useMemo(() => columns.map((col) => calculateWidthPercentage(col.colNum, totalTimesheetHeaderWidths + 0.1)), []);

  return (
    <Table.Header>
      <Table.HeaderItem width="auto">
        <Checkbox
          name="bulk-select"
          label="Bulk select"
          hideLabel
          onChange={() => selectTimesheetRow(
            id,
            !isChecked,
          )}
          checked={isChecked}
          indeterminate={
            selectedCount > 0 && selectedCount !== selectableCount
          }
          disabled={allTimesheetsAreApproved(timesheets)}
        />
      </Table.HeaderItem>
      {columns.map((c, i) => (
        <Table.HeaderItem width={totalTimesheetRowWidths[i]} key={c.key}>
          {c.columnName}
        </Table.HeaderItem>
      ))}
    </Table.Header>
  );
};
